import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Video, Wallet } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export const NotificationBell = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(() => {
    api
      .get("/notifications")
      .then((r) => {
        setItems(r.data.notifications || []);
        setUnread(r.data.unread || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const onOpenChange = (open) => {
    if (open && unread > 0) {
      api.post("/notifications/read").then(() => setUnread(0)).catch(() => {});
    }
  };

  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full text-white hover:bg-white/10 hover:text-white" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground"
              data-testid="notification-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="border-b border-border/70 px-4 py-3 font-display text-sm font-bold">
          {t("notifications")}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground" data-testid="no-notifications">
              {t("noNotifications")}
            </p>
          )}
          {items.map((n) => (
            <div key={n.id} className="border-b border-border/50 px-4 py-3 last:border-b-0" data-testid={`notification-${n.id}`}>
              <div className="flex items-start gap-2">
                <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                  {n.type === "fee_overdue" ? <Wallet className="h-4 w-4" /> : <Video className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  {n.type === "fee_overdue" ? (
                    <>
                      <p className="text-sm font-semibold leading-snug">{t("feeOverdueNotif")}</p>
                      <p className="text-xs text-muted-foreground">
                        {n.fee_name} · {n.student_name}
                      </p>
                      <p className="num text-xs text-muted-foreground">
                        {(n.currency || "myr").toUpperCase()} {Number(n.amount).toFixed(2)} · {t("dueDate")}: {n.due_date}
                      </p>
                      <Link to="/parent/fees">
                        <Button size="sm" className="mt-2 h-8 rounded-full gap-1.5" data-testid={`pay-now-${n.id}`}>
                          <Wallet className="h-3.5 w-3.5" /> {t("payNow")}
                        </Button>
                      </Link>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold leading-snug">{t("classStartingSoon")}</p>
                      <p className="text-xs text-muted-foreground">
                        {n.class_name}
                        {n.student_name ? ` · ${n.student_name}` : ""}
                      </p>
                      <p className="num text-xs text-muted-foreground">
                        {n.date} · {n.start_time}
                      </p>
                      {n.meeting_link && (
                        <a href={n.meeting_link} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" className="mt-2 h-8 rounded-full gap-1.5" data-testid={`join-meet-${n.id}`}>
                            <Video className="h-3.5 w-3.5" /> {t("joinMeet")}
                          </Button>
                        </a>
                      )}
                    </>
                  )}
                </div>
                {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
